import {useQueryErrorResetBoundary} from '@tanstack/react-query';
import {Link, useRouter} from '@tanstack/react-router';
import {useCallback, useEffect} from 'react';

import {PageLayout} from '../../_old/PageLayout/PageLayout';
import {extractErrorMessage} from '../../functions/extractErrorMessage';
import {handleError} from '../../functions/handleError';
import {AppError} from '../../utils/errors';
import {Heading} from '../Heading';
import {Text} from '../Text';

import {ErrorScreenContainer, ErrorScreenIcon} from './common';
import {ERROR_KIND_CONFIG} from './errorKindConfig';
import {isLocationSegmentationRequired} from './locationSegmentationRequired';

import type {ErrorRouteComponent} from '@tanstack/react-router';
import type {ComponentProps} from 'react';

type Props = ComponentProps<ErrorRouteComponent>;
export function RouteError({reset, error}: Props): React.JSX.Element {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  useEffect(() => {
    handleError(error);
  }, [error]);

  const handleRetry = useCallback(() => {
    reset();
    void router.invalidate();
  }, [reset, router]);

  const kind = error instanceof AppError ? error.kind : 'critical';
  const title =
    (error instanceof AppError ? error.title : undefined) ??
    ERROR_KIND_CONFIG[kind].defaultTitle;
  const {icon, iconClassName} = ERROR_KIND_CONFIG[kind];

  return (
    <PageLayout.Main>
      <PageLayout.Content>
        <ErrorScreenContainer>
          <ErrorScreenIcon icon={icon} className={iconClassName} />

          <div className="space-y-4 text-center text-slate12">
            <Heading as="title" tag="h1">
              {title}
            </Heading>

            <Text>{extractErrorMessage(error)}</Text>

            {isLocationSegmentationRequired(error) ? (
              <Link className="btn-main" to="/locations">
                Complete location segmentation
              </Link>
            ) : (
              <button className="btn-main" onClick={handleRetry}>
                Reload
              </button>
            )}
          </div>
        </ErrorScreenContainer>
      </PageLayout.Content>
    </PageLayout.Main>
  );
}

RouteError.displayName = 'RouteError';
